import { useEffect, useMemo, useState } from "react";

import { buildBulkPickDestination } from "../bulk-pick-url";
import type { PlannerRegion } from "../regional-destination";
import type { IntegratedPlanGroup } from "../types";
import { Banner } from "./Feedback";

interface SelectedPlanGroupsProps {
  groups: readonly IntegratedPlanGroup[];
  bulkPickBaseUrl: string;
  plannerOrigin: string;
  localDevelopmentRegion: PlannerRegion | undefined;
}

interface ActionFeedback {
  tone: "success" | "error";
  title: string;
  detail?: string;
}

const PAGE_SIZE = 50;
const NUMBER_FORMAT = new Intl.NumberFormat("en-SG", { maximumFractionDigits: 2 });

export function SelectedPlanGroups({
  groups,
  bulkPickBaseUrl,
  plannerOrigin,
  localDevelopmentRegion,
}: SelectedPlanGroupsProps) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [actionFeedback, setActionFeedback] = useState<ActionFeedback | null>(null);
  const filteredGroups = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("en-SG");
    if (normalized.length === 0) return groups;
    return groups.filter(
      (group) =>
        `group ${group.groupNumber}`.includes(normalized) ||
        group.orderNumbersText.toLocaleLowerCase("en-SG").includes(normalized),
    );
  }, [groups, query]);
  const pageCount = Math.max(1, Math.ceil(filteredGroups.length / PAGE_SIZE));
  const pageStart = (page - 1) * PAGE_SIZE;
  const visibleGroups = filteredGroups.slice(pageStart, pageStart + PAGE_SIZE);
  const firstVisible = filteredGroups.length === 0 ? 0 : pageStart + 1;
  const lastVisible = Math.min(pageStart + PAGE_SIZE, filteredGroups.length);

  useEffect(() => {
    setPage((current) => Math.min(current, pageCount));
  }, [pageCount]);

  async function copyOrderNumbers(group: IntegratedPlanGroup): Promise<void> {
    try {
      if (navigator.clipboard?.writeText === undefined) {
        throw new Error("Clipboard access is not available in this browser.");
      }
      await navigator.clipboard.writeText(group.orderNumbersText);
      setActionFeedback({
        tone: "success",
        title: `Copied ${group.orderCount} order ${group.orderCount === 1 ? "number" : "numbers"}`,
      });
    } catch {
      setActionFeedback({
        tone: "error",
        title: "Could not copy order numbers",
        detail: "Clipboard access was blocked. Select the order numbers manually instead.",
      });
    }
  }

  return (
    <div className="selected-plan-groups">
      <div className="selected-plan-toolbar">
        <div>
          <h3>Selected plan groups</h3>
          <p>
            The planner selected this plan automatically. No comparison or manual choice is needed.
          </p>
        </div>
        <label className="field group-search-field">
          <span>Search groups or order numbers</span>
          <input
            type="search"
            value={query}
            placeholder="e.g. Group 2 or an order number"
            onChange={(event) => {
              setQuery(event.currentTarget.value);
              setPage(1);
              setActionFeedback(null);
            }}
          />
        </label>
      </div>

      {actionFeedback !== null && (
        <Banner tone={actionFeedback.tone} title={actionFeedback.title}>
          {actionFeedback.detail}
        </Banner>
      )}

      {groups.length === 0 ? (
        <div className="empty-state" role="status">
          The selected plan contains no groups. Return to BoostWare and start again.
        </div>
      ) : (
        <div className="table-scroll selected-plan-table-scroll">
          <table className="selected-plan-table" aria-label="Selected pick plan groups">
            <caption className="visually-hidden">
              Automatically selected plan with order numbers ready to copy or open in BoostWare.
            </caption>
            <thead>
              <tr>
                <th scope="col">Group</th>
                <th scope="col">Orders</th>
                <th scope="col">Total Items</th>
                <th scope="col">Unique SKU</th>
                <th scope="col">Unique locations</th>
                <th scope="col">Route cost</th>
                <th scope="col">Order numbers</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleGroups.map((group) => {
                const destination = buildBulkPickDestination(
                  bulkPickBaseUrl,
                  group.pickingJobIds,
                  plannerOrigin,
                  localDevelopmentRegion,
                );
                return (
                  <tr key={group.groupNumber}>
                    <td data-label="Group">
                      <strong>Group {group.groupNumber}</strong>
                    </td>
                    <td data-label="Orders">{formatNumber(group.orderCount)}</td>
                    <td data-label="Total Items">
                      {group.totalItems === null || group.totalItems === undefined
                        ? "Unknown"
                        : formatNumber(group.totalItems)}
                    </td>
                    <td data-label="Unique SKU">{formatNumber(group.uniqueSkuCount)}</td>
                    <td data-label="Unique locations">{formatNumber(group.uniqueLocationCount)}</td>
                    <td data-label="Route cost">{formatNumber(group.routeCost)}</td>
                    <td className="order-numbers-cell" data-label="Order numbers">
                      <span>{group.orderNumbersText}</span>
                    </td>
                    <td className="copy-cell group-action-cell" data-label="Actions">
                      <div className="group-actions">
                        <button
                          className="button button-secondary copy-orders-button"
                          type="button"
                          aria-label={`Copy order numbers for Group ${group.groupNumber}`}
                          onClick={() => void copyOrderNumbers(group)}
                        >
                          <CopyIcon />
                          Copy
                        </button>
                        {destination.available ? (
                          <a
                            className="button button-primary bulk-pick-button"
                            href={destination.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Create Bulk Pick for Group ${group.groupNumber}`}
                            onClick={() => setActionFeedback(null)}
                          >
                            <OpenIcon />
                            Create Bulk Pick
                          </a>
                        ) : (
                          <button
                            className="button button-primary bulk-pick-button"
                            type="button"
                            aria-label={`Create Bulk Pick for Group ${group.groupNumber}`}
                            onClick={() =>
                              setActionFeedback({
                                tone: "error",
                                title: "Could not open Bulk Pick",
                                detail: destination.reason,
                              })
                            }
                          >
                            <OpenIcon />
                            Create Bulk Pick
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredGroups.length === 0 && (
                <tr>
                  <td className="table-empty-cell" colSpan={7}>
                    No groups match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {groups.length > 0 && (
        <nav className="pagination selected-plan-pagination" aria-label="Selected plan group pages">
          <span role="status">
            Showing {formatNumber(firstVisible)}–{formatNumber(lastVisible)} of{" "}
            {formatNumber(filteredGroups.length)} groups · Page {formatNumber(page)} of{" "}
            {formatNumber(pageCount)}
          </span>
          <div className="selected-plan-page-actions">
            <button
              className="button button-secondary"
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              ← Previous
            </button>
            <button
              className="button button-secondary"
              type="button"
              disabled={page >= pageCount}
              onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
            >
              Next →
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}

function OpenIcon() {
  return (
    <svg
      className="copy-icon"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M14 5h5v5" />
      <path d="m10 14 9-9" />
      <path d="M19 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      className="copy-icon"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="8" y="8" width="11" height="11" rx="2" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    </svg>
  );
}

function formatNumber(value: number): string {
  return NUMBER_FORMAT.format(value);
}
